import React, { Component } from 'react';
import styled from 'styled-components';
import Loader from 'react-loading';
import { Mutation } from 'react-apollo';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { themes, utils } from 'kb-shared';
import Button from '../../../components/Button/NewButton';
import { AbsoluteCentered } from '../../../styled';
import gql from 'graphql-tag';

const { KBContacts } = utils;
const { fonts } = themes;

interface PatientConsentSubmitResponseData {
  createPatientConsent: {
    id: string;
  };
}
const PATIENT_CONSENT_SUBMIT = gql`
  mutation createPatientConsent($consentId: Int!) {
    createPatientConsent(consentId: $consentId) {
      patientConsent {
        id
      }
    }
  }
`;

type PatientConsent = {
  id: string;
};

const Body = styled('div')`
  h1,
  h2,
  h3 {
    ${fonts.DomaineDisplay};
  }

  h1 {
    font-size: 48px;
    text-align: center;
    width: 100%;
  }

  h2 {
    font-size: 36px;
  }

  h3 {
    font-size: 24px;
  }

  ul {
    list-style-type: disc;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 40px;
  }

  p {
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
  }

  a,
  p,
  li {
    font-size: initial;
    ${fonts.FoundersGrotesk};
  }

  li {
    padding-bottom: 5px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: white;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 90%;
`;

const Divider = styled.div`
  border: solid 1px black;
  width: 100%;
  margin: 48px 0px 48px 0px;
`;

const ConsentText = styled.div`
  ${themes.fonts.DomaineDisplay};
  font-size: 24px;
  text-align: center;
  padding: 0px 24px 48px 24px;
  max-width: 646px;
`;

const ErrorText = styled.div`
  ${themes.fonts.DomaineDisplay};
  font-size: 18px;
  text-align: center;
  padding: 0px 24px 48px 24px;
  max-width: 646px;
`;

const ButtonWrapper = styled.div`
  padding: 0px 48px 0px 48px;
`;

type Props = RouteComponentProps & {
  consentLinkHTML: string | null;
  consentFormId: number;
  onAgree: (patientConsentId: string) => void;
};

type State = {
  consentHTML: string | null;
  loading: boolean;
  error: boolean;
};

class YourConsent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      consentHTML: null,
      loading: true,
      error: false
    };
    if (props.consentLinkHTML) {
      this.loadHTML(props.consentLinkHTML);
    }
  }

  loadHTML(link: string) {
    fetch(link)
      .then(response => {
        return response.text();
      })
      .then(html => {
        this.setState({
          consentHTML: html,
          loading: false
        });
      })
      .catch(() => {
        this.setState({
          error: true,
          loading: false
        });
      });
  }

  shouldComponentUpdate(nextProps: Props) {
    if (nextProps.consentLinkHTML && this.props.consentLinkHTML !== nextProps.consentLinkHTML) {
      this.loadHTML(nextProps.consentLinkHTML);
    }
    return true;
  }

  // @ts-ignore
  onDidAgree = res => {
    if (!res || !res.data || !res.data.createPatientConsent) return;
    this.props.onAgree(res.data.createPatientConsent.patientConsent.id);
  };

  renderLoader() {
    return (
      <Container>
        <AbsoluteCentered>
          <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
        </AbsoluteCentered>
      </Container>
    );
  }

  renderError() {
    return (
      <Container>
        <ErrorText>
          Sorry, we&apos;re unable to load this form.
          <br />
          Please try again, or contact us at{' '}
          <a className="link" href={`mailto:${KBContacts.navigatorEmail}`}>
            {KBContacts.navigatorEmail}
          </a>
          .
        </ErrorText>
        <ButtonWrapper>
          <Button
            text={'Return to dashboard'}
            onClick={() => {
              this.props.history.replace('/');
            }}
          />
        </ButtonWrapper>
      </Container>
    );
  }

  render() {
    if (this.state.error) {
      return this.renderError();
    }
    if (this.state.loading) {
      return this.renderLoader();
    }

    // @ts-ignore
    const BodyHTML = () => <Body dangerouslySetInnerHTML={{ __html: this.state.consentHTML }} />;

    return (
      <Container>
        <Content>
          <BodyHTML />
          <Divider />
          <ConsentText>
            By clicking “I consent”, I agree and acknowledge that I have read and understand the
            information provided above.
          </ConsentText>
          <Mutation<PatientConsentSubmitResponseData, { consentId: number }>
            mutation={PATIENT_CONSENT_SUBMIT}
          >
            {(submitConsent, { loading }: { loading: boolean }) => {
              return (
                <ButtonWrapper>
                  <Button
                    text={'I consent'}
                    loading={loading}
                    disabled={loading}
                    onClick={() => {
                      submitConsent({
                        variables: {
                          consentId: this.props.consentFormId
                        }
                      })
                        // @ts-ignore
                        .then(this.onDidAgree);
                    }}
                  />
                </ButtonWrapper>
              );
            }}
          </Mutation>
        </Content>
      </Container>
    );
  }
}

export default withRouter(YourConsent);
